import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NavigationState, TabBadgeUpdate, TabNavigationOptions } from '@/types/navigation';

const INITIAL_TAB = 'discover';
const MAX_HISTORY_LENGTH = 10;

const initialState: NavigationState = {
  activeTab: INITIAL_TAB,
  previousTab: null,
  tabHistory: [INITIAL_TAB],
  badges: {
    discover: '',
    conversations: '',
    settings: '',
  },
  isNavigating: false,
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    navigateToTab: (
      state,
      action: PayloadAction<{ 
        tabId: string; 
        options?: TabNavigationOptions 
      }>
    ) => {
      const { tabId, options = {} } = action.payload;
      
      // Don't navigate to the same tab unless it's a replace operation
      if (state.activeTab === tabId && options.direction !== 'replace') {
        return;
      }

      state.isNavigating = true;
      state.previousTab = state.activeTab;
      state.activeTab = tabId;

      // Update tab history unless prevented
      if (!options.preventHistory) {
        if (options.direction === 'backward') {
          // Remove current tab from history when going backward
          const currentIndex = state.tabHistory.lastIndexOf(state.previousTab);
          if (currentIndex > 0) {
            state.tabHistory = state.tabHistory.slice(0, currentIndex);
          }
        } else if (options.direction === 'replace') {
          // Replace last entry in history
          state.tabHistory[state.tabHistory.length - 1] = tabId;
        } else {
          // Add to history (forward navigation)
          state.tabHistory.push(tabId);
          
          // Keep history length manageable
          if (state.tabHistory.length > MAX_HISTORY_LENGTH) {
            state.tabHistory = state.tabHistory.slice(-MAX_HISTORY_LENGTH);
          }
        }
      }

      // Update badge if provided
      if (options.updateBadge) {
        const { tabId: badgeTabId, badge } = options.updateBadge;
        if (badge === null) {
          delete state.badges[badgeTabId];
        } else {
          state.badges[badgeTabId] = badge;
        }
      }
    },

    updateTabBadge: (state, action: PayloadAction<TabBadgeUpdate>) => {
      const { tabId, badge } = action.payload;
      
      if (badge === null || badge === undefined) {
        delete state.badges[tabId];
      } else {
        state.badges[tabId] = badge;
      }
    },

    updateMultipleTabBadges: (state, action: PayloadAction<TabBadgeUpdate[]>) => {
      action.payload.forEach(({ tabId, badge }) => {
        if (badge === null || badge === undefined) {
          delete state.badges[tabId];
        } else {
          state.badges[tabId] = badge;
        }
      });
    },

    clearTabBadge: (state, action: PayloadAction<string>) => {
      delete state.badges[action.payload];
    },

    clearAllBadges: (state) => {
      state.badges = {};
    },

    setNavigating: (state, action: PayloadAction<boolean>) => {
      state.isNavigating = action.payload;
    },

    goBack: (state) => {
      if (state.tabHistory.length > 1) {
        const previousTabId = state.tabHistory[state.tabHistory.length - 2];
        
        state.isNavigating = true;
        state.previousTab = state.activeTab;
        state.activeTab = previousTabId;
        
        // Remove current tab from history
        state.tabHistory.pop();
      }
    },

    resetNavigation: (state) => {
      state.activeTab = INITIAL_TAB;
      state.previousTab = null;
      state.tabHistory = [INITIAL_TAB];
      state.isNavigating = false;
    },

    clearHistory: (state) => {
      state.tabHistory = [state.activeTab];
      state.previousTab = null;
    },
  },
});

export const {
  navigateToTab,
  updateTabBadge,
  updateMultipleTabBadges,
  clearTabBadge,
  clearAllBadges,
  setNavigating,
  goBack,
  resetNavigation,
  clearHistory,
} = navigationSlice.actions;

export default navigationSlice.reducer;